<!--
	@component
	domain wrapper around AutocompleteInputField for selecting a user account
	(case owner / case manager)

	only hands svelecte a value once it carries an `id`. an unassigned account is
	`{ id: null, ... }`, a truthy object with no id; feeding that to a `valueAsObject`
	+ `strictMode:false` field makes svelecte fire an init-resolution fetch it cannot
	build a query for, so it ships `?qry=[query]` on mount and the backend answers 400
	(see docs/SVELECTE.md "minQuery"). the selection is mirrored back into the
	always-object `account` store value via onChange

	@param {UserAccountData} account - bindable, the selected user account (always an object)
	@param {boolean} disabled - disables the field
	@param {() => void} onChange - called after a selection or clear, e.g. to trigger autosave
	@see AutocompleteInputField
-->

<script lang="ts">
	import AutocompleteInputField from './AutocompleteInputField.svelte'
	import {
		getFormAcConfigFetch,
		type AutocompleteFieldConfig
	} from '$types/autocompleteFieldConfig'
	import { getUserAccountAcOptions } from '#lib/map/fak/utils/fakUtils.js'
	import {
		DefaultUserAccountData,
		userAccountAutocompleteRenderer,
		type UserAccountData
	} from '$types/userAccountData'
	import type { FontProps } from '$types/fontProps'

	interface Props {
		account: UserAccountData
		fontProps: FontProps
		label: string
		errorPath: string
		disabled?: boolean
		required?: boolean
		id?: string
		onChange?: () => void
	}

	let {
		account = $bindable(),
		fontProps,
		label,
		errorPath,
		disabled = false,
		required = false,
		id = 'user-account-ac-' + Math.random().toString(16).slice(2),
		onChange
	}: Props = $props()

	/**
	 * built once, not $derived: a new config object reference re-inits svelecte
	 * (see docs/SVELECTE.md "$derived configs"). disabled is driven by the prop
	 */
	const config: AutocompleteFieldConfig = {
		...getFormAcConfigFetch('user-accounts', false, 'id', 'text'),
		valueAsObject: true
	}

	/**
	 * computed once at mount, not $derived: a fresh array/object ref on every account
	 * change feeds svelecte and loops (see AddressDataAutocomplete's $state warning).
	 * the {#key} wrap at both consumers remounts this on store re-init, refreshing it
	 */
	const options = getUserAccountAcOptions(account)
</script>

<AutocompleteInputField
	value={account?.id ? account : null}
	{options}
	{disabled}
	{id}
	{fontProps}
	{label}
	{errorPath}
	{required}
	renderer={userAccountAutocompleteRenderer}
	autocompleteConfig={config}
	onChange={(selected) => {
		account = (selected as UserAccountData | null) ?? DefaultUserAccountData()
		onChange?.()
	}}
/>
